import Cookies from 'js-cookie';

const DEFAULT_METHOD = 'GET';
const CONTENT_TYPE_MARKDOWN = 'text/markdown';
const URLPATTERN = /\/([a-z]{2})(\/|$)/;

export const REVALIDATE = {
  revalidateIfStale: true,
  revalidateOnFocus: false,
  revalidateOnReconnect: false,
};

function getCurrentLocale(): string | null {
  if (typeof window === 'undefined') return null;
  return (
    Cookies.get('Next-Locale') ||
    window.location.href.match(URLPATTERN)?.[1] ||
    null
  );
}

const handleResponse = async (response: Response): Promise<any> => {
  if (!response.ok) {
    try {
      const error = await response.json();
      return Promise.reject({
        status: response.status,
        error: error.error || null,
        type: error.type || null,
      });
    } catch {
      return Promise.reject({
        status: response.status,
        error: null,
        type: null,
      });
    }
  }

  const contentType = response.headers.get('content-type');
  if (contentType?.includes(CONTENT_TYPE_MARKDOWN)) {
    return response.text();
  }

  const text = await response.text();
  return text === '' ? response : JSON.parse(text);
};

const baseFetcher = async (
  url: string,
  options: RequestInit = {},
  withToken = false
) => {
  const { assets, ...restOptions } = options as any;
  const lang = getCurrentLocale();

  const headers = new Headers(options.headers);
  headers.set('Lang', lang || '');

  if (withToken) {
    headers.set('Token', Cookies.get('sessionId') || '');
    headers.set('accept', '*/*');
  }

  const baseUrl = assets
    ? process.env.NEXT_PUBLIC_ASSETS
    : process.env.NEXT_PUBLIC_HOSTNAME;

  try {
    const response = await fetch(`${baseUrl}${url}`, {
      method: options.method || DEFAULT_METHOD,
      ...restOptions,
      headers,
    });
    return handleResponse(response);
  } catch (error: any) {
    const errorData = error.status
      ? error
      : {
          status: 0,
          error: error.message,
          type: 'NetworkError',
        };
    console.error('Request error:', errorData);
    return Promise.reject(errorData);
  }
};

export const fetcher = (url: string, options: RequestInit = {}) =>
  baseFetcher(url, options, false);

export const fetcherWithToken = (url: string, options: RequestInit = {}) =>
  baseFetcher(url, options, true);

export const multiFetcher = (urls: string[]) =>
  Promise.all(urls.map((url) => fetcher(url)));

export const sendRequest = (url: string, { arg }: { arg: any }) =>
  fetcher(url, {
    method: 'POST',
    body: JSON.stringify(arg),
  });
